import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createLink, listLinks, revokeLink } from '../../api/share.ts';
import { formatDate } from '../../lib/format.ts';
import { toast } from '../../store/toast.ts';
import { IconCopy, IconLink, IconTrash } from '../../lib/icons.tsx';
import type { ApiNode } from '../../api/types.ts';

export function ShareTab({ namespaceId, node }: { namespaceId: string; node: ApiNode }) {
  const queryClient = useQueryClient();
  const [password, setPassword] = useState('');
  const [expiresAt, setExpiresAt] = useState('');

  const { data } = useQuery({ queryKey: ['share-links'], queryFn: listLinks });
  const links = (data?.items ?? []).filter((l) => l.path === node.path);

  const create = useMutation({
    mutationFn: () => createLink({
      namespace_id: namespaceId,
      path: node.path,
      allow_download: true,
      allow_upload: false,
      ...(password ? { password } : {}),
      ...(expiresAt ? { expires_at: new Date(`${expiresAt}T23:59:59`).toISOString() } : {}),
    }),
    onSuccess: (result) => {
      void navigator.clipboard?.writeText(result.url);
      toast.success('링크를 만들고 클립보드에 복사했습니다');
      setPassword(''); setExpiresAt('');
      void queryClient.invalidateQueries({ queryKey: ['share-links'] });
    },
    onError: (e: Error) => { toast.error(e); },
  });

  const revoke = useMutation({
    mutationFn: revokeLink,
    onSuccess: () => {
      toast.info('링크를 폐기했습니다');
      void queryClient.invalidateQueries({ queryKey: ['share-links'] });
    },
    onError: (e: Error) => { toast.error(e); },
  });

  return (
    <div>
      <div className="field">
        <label htmlFor="share-pw">비밀번호 (선택)</label>
        <input
          id="share-pw" className="input" type="password" autoComplete="new-password"
          placeholder="설정하지 않으면 링크만으로 열람"
          value={password} onChange={(e) => { setPassword(e.target.value); }}
        />
      </div>
      <div className="field">
        <label htmlFor="share-exp">만료일 (선택)</label>
        <input
          id="share-exp" className="input" type="date"
          value={expiresAt} onChange={(e) => { setExpiresAt(e.target.value); }}
        />
      </div>
      <button
        className="btn btn--primary btn--block"
        onClick={() => { create.mutate(); }}
        disabled={create.isPending}
      >
        <IconLink size={16} /> 공개 링크 만들기
      </button>

      {links.length > 0 && (
        <>
          <div className="sidebar__section" style={{ paddingLeft: 0 }}>기존 링크</div>
          {links.map((link) => (
            <div key={link.id} className="row" style={{ marginBottom: 8 }}>
              <div className="spacer" style={{ minWidth: 0 }}>
                <div className="truncate mono">{link.url}</div>
                <div className="faint" style={{ fontSize: 11 }}>
                  조회 {link.view_count} · 다운로드 {link.download_count}
                  {link.has_password && ' · 비밀번호'}
                  {link.expires_at && ` · ${formatDate(link.expires_at)}까지`}
                </div>
              </div>
              <button
                className="btn btn--sm btn--icon" aria-label="링크 복사"
                onClick={() => {
                  void navigator.clipboard?.writeText(link.url);
                  toast.success('복사했습니다');
                }}
              >
                <IconCopy size={14} />
              </button>
              <button
                className="btn btn--sm btn--icon" aria-label="링크 폐기"
                onClick={() => { revoke.mutate(link.id); }}
              >
                <IconTrash size={14} />
              </button>
            </div>
          ))}
        </>
      )}
    </div>
  );
}
